import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { useCreateLead } from '@/application/services/lead.service'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/ui/components/common/Dialog'
import { Button } from '@/ui/components/common/Button'
import { Input } from '@/ui/components/forms/Input'
import { RichTextEditor } from '@/ui/components/common/RichTextEditor'
import { ImageField } from '@/ui/components/forms/ImageField'

const makeAddLeadSchema = (
  translate: (key: string, opts?: Record<string, unknown>) => string,
  descriptionRequired: boolean,
) =>
  z.object({
    title: z
      .string()
      .min(1, translate('validation.required'))
      .max(200, translate('validation.max_chars', { count: 200 })),
    description: descriptionRequired
      ? z
          .string()
          .min(1, translate('validation.description_required'))
          .max(20000, translate('validation.max_chars', { count: 20000 }))
      : z
          .string()
          .max(20000, translate('validation.max_chars', { count: 20000 }))
          .optional()
          .or(z.literal('')),
  })

type AddLeadFormData = z.infer<ReturnType<typeof makeAddLeadSchema>>

interface CreateLeadDialogProps {
  investigationId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Management lets an author skip the description; the public "add lead"
   * form requires one. Kept as a prop rather than unified, since it is a
   * genuine behaviour difference between the two surfaces, not an oversight.
   */
  descriptionRequired?: boolean
  /** Only the management surface offers an image field on creation. */
  showImageField?: boolean
  /** The public surface labels the cancel action "back" rather than "cancel". */
  cancelLabelKey?: 'common.cancel' | 'common.back'
}

/**
 * Shared "create lead" dialog, used by the management `LeadsSection` and the
 * public `InvestigationLeadsSection`. Owns its own `useForm` + zod schema and
 * `useCreateLead` wiring, following the `EditLeadDialog` precedent.
 */
export function CreateLeadDialog({
  investigationId,
  open,
  onOpenChange,
  descriptionRequired = true,
  showImageField = false,
  cancelLabelKey = 'common.cancel',
}: CreateLeadDialogProps) {
  const { t } = useTranslation()
  const { mutateAsync: createLead } = useCreateLead()
  const schema = useMemo(() => makeAddLeadSchema(t, descriptionRequired), [t, descriptionRequired])

  const [imageUrl, setImageUrl] = useState('')

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AddLeadFormData>({ resolver: zodResolver(schema) })

  const onSubmit = async (data: AddLeadFormData) => {
    await createLead({
      title: data.title,
      investigationId,
      description: data.description || '',
      ...(showImageField ? { imageUrl: imageUrl || null } : {}),
    })
    reset()
    setImageUrl('')
    onOpenChange(false)
    toast.success(t('investigation_detail.add_lead_success'))
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen)
        if (!nextOpen) {
          reset()
          setImageUrl('')
        }
      }}
    >
      <DialogContent size="wide">
        <DialogHeader>
          <DialogTitle className="font-serif">
            {t('investigation_detail.add_lead_title')}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              {t('investigation_detail.add_lead_title_label')}
              <span className="text-destructive ml-1">*</span>
            </label>
            <Input
              {...register('title')}
              placeholder={t('investigation_detail.add_lead_title_placeholder')}
            />
            {errors.title && <p className="text-destructive text-xs">{errors.title.message}</p>}
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              {t('investigation_detail.add_lead_description_label')}
              {descriptionRequired && <span className="text-destructive ml-1">*</span>}
            </label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <RichTextEditor
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  placeholder={t('investigation_detail.add_lead_description_placeholder')}
                  onUploadError={(message) => toast.error(message)}
                />
              )}
            />
            {errors.description && (
              <p className="text-destructive text-xs">{errors.description.message}</p>
            )}
          </div>
          {showImageField && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t('admin.form_image_label')}</label>
              <ImageField value={imageUrl} onChange={setImageUrl} />
            </div>
          )}
          <DialogFooter className="pt-2">
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={isSubmitting}>
                {t(cancelLabelKey)}
              </Button>
            </DialogClose>
            <Button type="submit" isLoading={isSubmitting}>
              {t('investigation_detail.add_lead_submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
